import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  ViewEncapsulation,
  inject,
  output,
  viewChildren,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { from, switchMap } from 'rxjs';
import {
  MatExpansionPanel,
  MatAccordion,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';

import { RouterResolveStatusService } from '@shared/services/router-resolve-status.service';
import { MainNavService } from './main-nav.service';
import { AuthenticationService } from '@authentication/authentication.service';
import { MainNavLinkCollection } from './main-nav-links';
import { FeatureFlagService } from '@shared/services/feature-flag.service';
import { OfficeHoursService } from '@shared/services/office-hours/office-hours.service';
import { animateText } from './main-nav-animations';
import { linkCollections } from './router-links';
import { MatNavList } from '@angular/material/list';
import { NavLinkComponent } from './nav-link/nav-link.component';
import { MatDivider } from '@angular/material/divider';
import { NgTemplateOutlet } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { BreakpointService } from '@shared/services/breakpoint.service';

@Component({
  selector: 'app-main-nav',
  templateUrl: './main-nav.component.html',
  styleUrl: './main-nav.component.scss',
  animations: [animateText],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatNavList,
    NavLinkComponent,
    MatDivider,
    NgTemplateOutlet,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatIcon,
  ],
})
export class MainNavComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private authenticationService = inject(AuthenticationService);
  private breakpointService = inject(BreakpointService);
  routerResolveStatus = inject(RouterResolveStatusService);
  mainNavService = inject(MainNavService);
  featureFlagService = inject(FeatureFlagService);
  officeHoursService = inject(OfficeHoursService);

  readonly closeDrawer = output();
  readonly adminLinks = viewChildren<ElementRef>('adminLink');
  readonly expansionPanels = viewChildren(MatExpansionPanel);

  readonly expansionPanelStates = toSignal(this.mainNavService.expansionPanelStates$, { requireSync: true });

  readonly linkCollections = toSignal(
    this.authenticationService.getUserScopes$().pipe(switchMap(() => from(this.mainNavService.getMainNavLinks()))),
    { initialValue: [] as MainNavLinkCollection[] }
  );
  public allLinkCollections = linkCollections;
  public title: string;
  public isAustralia = false;

  ngOnInit(): void {
    this.mainNavService.mainNavOpen$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((open) => {
      const expansionPanels = this.expansionPanels();
      if (!open && expansionPanels) {
        for (const accordion of expansionPanels) {
          accordion.close();
        }
      }
    });
  }

  public openMenu(): void {
    if (!this.mainNavService.mainNavFixedOpen$.value && !this.mainNavService.mainNavOpen$.value) {
      const mainNavOpen = !this.mainNavService.mainNavOpen$.value;
      this.mainNavService.mainNavOpen$.next(mainNavOpen);
    }
  }

  public closeMenu(): void {
    if (
      !this.mainNavService.mainNavFixedOpen$.value &&
      this.mainNavService.mainNavOpen$.value &&
      this.breakpointService.isMatched('gt-xs')
    ) {
      const mainNavOpen = !this.mainNavService.mainNavOpen$.value;
      this.mainNavService.mainNavOpen$.next(mainNavOpen);

      for (const accordion of this.expansionPanels()) {
        accordion.close();
      }
    }
  }
}
